import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { isEqual, size } from 'lodash';

import Alert from '../Alert';
import Heading from '../Heading';
import { Stack } from '../LayoutPrimitives';

const errorItemType = PropTypes.shape({
    errorText: PropTypes.string,
    inputId: PropTypes.string,
});

export default class FormValidationAlert extends React.PureComponent {
    static propTypes = {
        /** The error(s) to display. Either an array of validation objects or
         * an object where each key is the form section and the corresponding
         * value is an array of validation objects */
        errors: PropTypes.oneOfType([
            PropTypes.arrayOf(errorItemType),
            PropTypes.objectOf(PropTypes.arrayOf(errorItemType)),
        ]),

        /** The heading level used for the alert's title */
        headingLevel: PropTypes.number,

        /** Additional className applied to the outer container */
        className: PropTypes.string,
    };

    static defaultProps = {
        errors: undefined,
        headingLevel: 3,
        className: undefined,
    };

    constructor(props) {
        super(props);

        this.outerRef = React.createRef();
    }

    componentDidMount() {
        this.focusAlertContainer();
    }

    componentDidUpdate(prevProps) {
        const { errors } = this.props;

        if (!isEqual(errors, prevProps.errors)) {
            this.focusAlertContainer();
        }
    }

    getErrorCount = () => {
        const { errors } = this.props;
        let count = 0;

        const keys = Object.keys(errors);

        keys.forEach((key) => {
            count += errors[key].length;
        });

        return count;
    };

    focusAlertContainer = () => {
        const { errors } = this.props;

        const hasErrors = size(errors) > 0;

        if (hasErrors && this.outerRef.current) {
            this.outerRef.current.focus();
        }
    };

    focusId = (id) => (event) => {
        const element = document.getElementById(id);

        if (element && element.focus) {
            event.preventDefault();
            element.focus();
        }
    };

    renderErrorsArray = () => {
        const { errors } = this.props;

        const ListTag = errors.length === 1 ? 'ul' : 'ol';

        return (
            <ListTag>
                {errors.map((item) => (
                    <li key={item.inputId}>
                        <a
                            onClick={this.focusId(item.inputId)}
                            href={`#${item.inputId}`}
                            title="Jump to the field with this error"
                        >
                            {item.errorText}
                        </a>
                    </li>
                ))}
            </ListTag>
        );
    };

    renderErrorsObject = () => {
        const { errors, headingLevel } = this.props;

        const sectionNames = Object.keys(errors);
        const countBySection = {};

        sectionNames.forEach((section, index) => {
            countBySection[index] = errors[section].length;
        });

        return sectionNames.map((section, index) => {
            let startNumber = 1;

            if (index > 0) {
                for (let i = 0; i < index; i += 1) {
                    startNumber += countBySection[i];
                }
            }

            return (
                <div className="sarsa-form-validation-alert-section" key={section}>
                    <Heading headingLevel={headingLevel + 1} appearance="h6">
                        {section}
                    </Heading>
                    <ol start={`${startNumber}`}>
                        {errors[section].map((error) => (
                            <li key={error.inputId}>
                                <a
                                    onClick={this.focusId(error.inputId)}
                                    href={`#${error.inputId}`}
                                    title="Jump to the field with this error"
                                >
                                    {error.errorText}
                                </a>
                            </li>
                        ))}
                    </ol>
                </div>
            );
        });
    };

    render() {
        const { errors, headingLevel, className, ...rest } = this.props;

        if (!errors) {
            return null;
        }

        const errorsIsArray = Array.isArray(errors);
        const errorsCount = errorsIsArray ? errors.length : this.getErrorCount();

        if (!errorsCount) {
            return null;
        }

        const heading =
            errorsCount === 1
                ? 'To continue, correct the error below'
                : `To continue, correct the ${errorsCount} errors below`;

        const errorList = errorsIsArray
            ? this.renderErrorsArray()
            : this.renderErrorsObject();

        return (
            <div
                data-component="FormValidationAlert"
                tabIndex="-1"
                ref={this.outerRef}
                className={cx('sarsa-form-validation-alert', className)}
                {...rest}
            >
                <Alert type="error" heading={heading} headingLevel={headingLevel}>
                    <div className="inner-error-wrap">
                        <Stack>{errorList}</Stack>
                    </div>
                </Alert>
            </div>
        );
    }
}
